import {
  Button,
  ButtonGroup,
  Icon,
  Modal,
  Stack,
  TextBody,
  TextTitle,
} from '@pypestream/design-system';
import {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SETTINGS_CSS } from './settings.styles';

export interface SettingsSaveBarCopy {
  /** The bar's landmark name: "Unsaved changes". */
  label: string;
  /** "There are unsaved changes". */
  unsaved: string;
  /** "3 fields need attention before saving." */
  invalid: (count: number) => string;
  /** "2 groups weren't saved." */
  notSaved: (count: number) => string;
  cancel: string;
  save: string;
  /** The confirm shown when the user leaves with the bar up. */
  leave: { title: string; body: string; confirm: string; stay: string };
}

/** The shape of react-router's `useBlocker` result this provider needs. */
export interface SettingsLeaveBlocker {
  state: 'unblocked' | 'blocked' | 'proceeding';
  proceed?: () => void;
  reset?: () => void;
}

const neverBlock = (): SettingsLeaveBlocker => ({ state: 'unblocked' });

/** What a card hands the page's save bar. Read through a ref, so it never re-renders the page. */
export interface SettingsCardHandle {
  save: () => Promise<boolean>;
  cancel: () => void;
  focusFirstInvalid: () => void;
  focusHeading: () => void;
  element: () => Element | null;
}

export interface SettingsCardStatus {
  dirty: boolean;
  invalidCount: number;
}

interface SettingsFormContextValue {
  attach: (group: string, handle: RefObject<SettingsCardHandle>) => () => void;
  report: (group: string, status: SettingsCardStatus) => void;
  saving: boolean;
}

const SettingsFormContext = createContext<SettingsFormContextValue | null>(
  null
);

/** Null outside a provider: the card then keeps a Save and Cancel of its own. */
export const useSettingsForm = () => useContext(SettingsFormContext);

const inDocumentOrder = (a: Element | null, b: Element | null) =>
  a && b && a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_PRECEDING
    ? 1
    : -1;

export interface SettingsFormProviderProps {
  copy: SettingsSaveBarCopy;
  /** After every dirty card saved. */
  onSaved?: () => void;
  /**
   * The app's `useBlocker`, passed in so this package needs no router of its
   * own. Omit it where the page already owns the router's one blocker, and feed
   * `onDirtyChange` to that blocker instead.
   */
  useLeaveBlocker?: (when: boolean) => SettingsLeaveBlocker;
  onDirtyChange?: (dirty: boolean) => void;
  children: ReactNode;
}

/** One sticky save bar for every settings card inside it. */
export function SettingsFormProvider({
  copy,
  onSaved,
  useLeaveBlocker = neverBlock,
  onDirtyChange,
  children,
}: SettingsFormProviderProps) {
  const handles = useRef(new Map<string, RefObject<SettingsCardHandle>>());
  const [statuses, setStatuses] = useState<Record<string, SettingsCardStatus>>(
    {}
  );
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(0);
  const [announcement, setAnnouncement] = useState('');
  // A second click lands before `saving` re-renders; a ref sees it.
  const inFlight = useRef(false);

  const attach = useCallback(
    (group: string, handle: RefObject<SettingsCardHandle>) => {
      handles.current.set(group, handle);
      return () => {
        handles.current.delete(group);
        setStatuses(({ [group]: _gone, ...rest }) => rest);
      };
    },
    []
  );

  const report = useCallback((group: string, status: SettingsCardStatus) => {
    setStatuses((current) => {
      const previous = current[group];
      if (
        previous?.dirty === status.dirty &&
        previous.invalidCount === status.invalidCount
      )
        return current;
      return { ...current, [group]: status };
    });
  }, []);

  const dirtyGroups = Object.keys(statuses).filter((g) => statuses[g].dirty);
  const invalidCount = Object.values(statuses).reduce(
    (sum, s) => sum + s.invalidCount,
    0
  );
  const visible = dirtyGroups.length > 0;
  const blocker = useLeaveBlocker(visible);
  const blocked = blocker.state === 'blocked';
  const [answered, setAnswered] = useState(false);
  const leaving = useRef(false);
  useEffect(() => {
    if (!blocked) setAnswered(false);
  }, [blocked]);

  // ps-modal portals itself out while open, so navigating with it open would
  // unmount a node React no longer owns: close first, then act on the answer.
  const answer = (leave: boolean) => {
    leaving.current = leave;
    setAnswered(true);
  };
  const settle = () => {
    if (!blocked) return;
    const leave = leaving.current;
    leaving.current = false;
    if (leave) blocker.proceed?.();
    else blocker.reset?.();
  };

  useEffect(() => {
    if (!visible) return undefined;
    onDirtyChange?.(true);
    return () => onDirtyChange?.(false);
  }, [onDirtyChange, visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const hold = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', hold);
    return () => window.removeEventListener('beforeunload', hold);
  }, [visible]);

  useEffect(() => {
    setAnnouncement(visible ? copy.unsaved : '');
    if (!visible) setFailed(0);
    // Announce on appear only; copy changing under an open bar is not news.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handlesOf = (groups: string[]) =>
    groups
      .map((group) => [group, handles.current.get(group)?.current] as const)
      .filter((entry): entry is [string, SettingsCardHandle] =>
        Boolean(entry[1])
      )
      .sort(([, a], [, b]) => inDocumentOrder(a.element(), b.element()));

  const save = async () => {
    if (inFlight.current) return;
    if (invalidCount > 0) {
      const invalid = Object.keys(statuses).filter(
        (g) => statuses[g].invalidCount > 0
      );
      handlesOf(invalid)[0]?.[1].focusFirstInvalid();
      return;
    }
    const targets = handlesOf(dirtyGroups);
    inFlight.current = true;
    setSaving(true);
    setFailed(0);
    const results = await Promise.all(
      targets.map(([, handle]) => handle.save().catch(() => false))
    );
    inFlight.current = false;
    setSaving(false);
    const failures = targets.filter((_, i) => !results[i]);
    if (failures.length === 0) {
      onSaved?.();
      return;
    }
    setFailed(failures.length);
    setAnnouncement(copy.notSaved(failures.length));
    failures[0][1].focusHeading();
  };

  const cancel = () =>
    handlesOf(dirtyGroups).forEach(([, handle]) => handle.cancel());

  const context = useMemo(
    () => ({ attach, report, saving }),
    [attach, report, saving]
  );

  const message =
    invalidCount > 0
      ? copy.invalid(invalidCount)
      : failed > 0
        ? copy.notSaved(failed)
        : copy.unsaved;

  return (
    <SettingsFormContext.Provider value={context}>
      <style>{SETTINGS_CSS}</style>
      {/* A block of its own: sticky travels only within its parent, and a page
          body slot can be an inline box with no height. */}
      <div className="ui-settings-form">
        {children}
        {visible && (
          <div className="ui-settings-bar-wrap">
            <div
              className="ui-settings-bar"
              role="region"
              aria-label={copy.label}
              data-test-id="settings-save-bar"
            >
              <span
                className={
                  invalidCount > 0 || failed > 0
                    ? 'ui-settings-bar__message ui-settings-bar__message--error'
                    : 'ui-settings-bar__message'
                }
              >
                {(invalidCount > 0 || failed > 0) && (
                  <Icon name="warning" size="small" aria-hidden="true" />
                )}
                {message}
              </span>
              <Button
                variant="ghost"
                disabled={saving}
                test-id="settings-bar-cancel"
                onClick={cancel}
              >
                {copy.cancel}
              </Button>
              {/* Not `disabled`: a blocked Save stays reachable and takes the
                user to the field in the way (DS Button cannot carry aria-disabled). */}
              <Button
                loading={saving}
                className={
                  invalidCount > 0
                    ? 'ui-settings-bar__save--blocked'
                    : undefined
                }
                test-id="settings-bar-save"
                onClick={() => void save()}
              >
                {copy.save}
              </Button>
            </div>
          </div>
        )}
      </div>
      <span className="ui-visually-hidden" role="status" aria-live="polite">
        {announcement}
      </span>
      <Modal open={blocked && !answered} size="medium" onClose={settle}>
        <Stack slot="header" direction="column" gutter="small">
          <TextTitle size="small">{copy.leave.title}</TextTitle>
          <TextBody variant="secondary">{copy.leave.body}</TextBody>
        </Stack>
        <ButtonGroup slot="footer" align="end">
          <Button
            variant="secondary"
            test-id="settings-leave-stay"
            onClick={() => answer(false)}
          >
            {copy.leave.stay}
          </Button>
          <Button
            variant="danger"
            test-id="settings-leave-confirm"
            onClick={() => answer(true)}
          >
            {copy.leave.confirm}
          </Button>
        </ButtonGroup>
      </Modal>
    </SettingsFormContext.Provider>
  );
}
